/**
 * Shared types and view helpers for the Affiliate Profile → Personal Info tab.
 * The real values are loaded via `useAffiliate(id)` and bridged through
 * `operatorDetailAdapter`; this file owns the FE-side shapes and a couple of
 * formatting helpers that are tab-agnostic enough to live next to the types.
 */

export type AffiliatePaymentRevShareScope =
  | "sportsbook"
  | "casino"
  | "sportsbook_and_casino";

export type AffiliatePaymentCpaActionType =
  | "user_signups"
  | "first_time_deposits";

export type AffiliatePaymentCpa = {
  type: "cpa";
  /** Payout in USD per acquisition (e.g. $50). */
  amountUsd: number;
  // null = legacy plan, card falls back to the Signup label
  actionType: AffiliatePaymentCpaActionType | null;
};

export type AffiliatePaymentFlatFee = {
  type: "flat_fee";
  /** Flat payout in USD. */
  amountUsd: number;
};

type AffiliatePaymentRevShareCommon = {
  type: "rev_share";
  scope: AffiliatePaymentRevShareScope;
  minimumSportsbookProfitUsd?: number;
  minimumCasinoProfitUsd?: number;
};

export type AffiliatePaymentRevShareSingle = AffiliatePaymentRevShareCommon & {
  percentage: number;
};

// Per-product percentages, used when SB and Casino pay different rates.
// Optional so the card can render whichever side has a value.
export type AffiliatePaymentRevShareSplit = AffiliatePaymentRevShareCommon & {
  sportsbookPercentage?: number;
  casinoPercentage?: number;
};

export type AffiliatePaymentRevShare =
  | AffiliatePaymentRevShareSingle
  | AffiliatePaymentRevShareSplit;

export type AffiliatePaymentLeafPlan =
  | AffiliatePaymentCpa
  | AffiliatePaymentFlatFee
  | AffiliatePaymentRevShare;

export type AffiliatePaymentHybrid = {
  type: "hybrid";
  /** Whether the card opens already expanded the first time it renders. */
  defaultOpen?: boolean;
  components: AffiliatePaymentLeafPlan[];
};

export type AffiliatePaymentPlan =
  | AffiliatePaymentLeafPlan
  | AffiliatePaymentHybrid;

export type AffiliateContract = {
  signedAt: string;
  renewalAt: string;
  fileName: string;
  fileUrl: string;
};

export type AffiliateAddress = {
  line1: string;
  line2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
};

export type AffiliatePersonalInfo = {
  affiliateId: string;
  fullName: string;
  email: string;
  joinedAt: string;
  userId: string;
  websiteUrl: string;
  address: AffiliateAddress;
  payment?: AffiliatePaymentPlan;
  contract: AffiliateContract;
};

/**
 * Build initials for the avatar circle. Falls back to a single `?` when the
 * name is empty so the avatar never renders blank.
 */
export function getAffiliateInitials(fullName: string | undefined): string {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

/**
 * Format an address into the two visual lines used in the Personal Info card:
 *   line1: street (and optional secondary line)
 *   line2: city, region, postal code
 *   line3: country
 */
export function formatAffiliateAddress(address: AffiliateAddress): string[] {
  const street = address.line2
    ? `${address.line1}, ${address.line2}`
    : address.line1;
  const cityLine = `${address.city}, ${address.region} ${address.postalCode}`;
  return [street, cityLine, address.country];
}
